import {FieldNode, ReportDefinitionV1} from '../contracts/report-definition.types';

/** Flattens a field tree to its leaf dot-paths, in tree order (depth-first).
 *
 * The leaves ARE the report's columns: what the table renders, what the Columns dropdown offers,
 * and the keys the row mapper files each cell under. It lives here, apart from any of those three,
 * so the mapper can read it without pulling the table's React dependencies into a pure data module. */
export function flattenFieldPaths(fields: FieldNode[]): string[] {
	const paths: string[] = [];

	const visit = (node: FieldNode, prefix: string): void => {
		const path = prefix ? `${prefix}.${node.name}` : node.name;
		if (node.children && node.children.length > 0) {
			for (const child of node.children) {
				visit(child, path);
			}
		} else {
			paths.push(path);
		}
	};

	for (const field of fields) {
		visit(field, '');
	}

	return paths;
}

/** The field-tree root of the task grain, and the fallback for a definition that names none. */
export const TASK_GRAIN_ROOT = 'task';

/** The root a SINGLE field name names, or null when it names none.
 *
 * Reads the first dot-segment, because a definition does not always arrive as the tree the contract
 * specifies: the drafting agent emits some with every column as one flat node whose name is the
 * whole dotted path (`{name:'projectStatus.project.info'}`). Both shapes name the same root in
 * their first segment.
 *
 * Null rather than a fallback, because the two callers need different answers for an unusable name.
 * `grainRootOf` is asked what a whole definition is keyed under and answers `task`, the meaning an
 * absent grain has always carried. A caller scanning field by field needs "no root here, keep
 * looking" instead: given `task` it would match the task root and stop, so one malformed field
 * would decide the grain for the whole definition. */
export function fieldRootOf(name: unknown): string | null {
	if (typeof name !== 'string') {
		return null;
	}
	const dot = name.indexOf('.');
	const root = dot === -1 ? name : name.slice(0, dot);
	return root === '' ? null : root;
}

/** The field-tree root a definition's rows and columns are keyed under - `task`, `projectStatus`.
 *
 * A definition with no fields, or whose first field names no usable root, keeps `task` - the
 * meaning every definition predating the grain concept carries. Read from the definition rather
 * than from a returned row: a fetch that nests its rows renames them, so the row cannot be trusted
 * to say what it is. */
export function grainRootOf(definition: Pick<ReportDefinitionV1, 'fields'>): string {
	return fieldRootOf(definition.fields?.[0]?.name) ?? TASK_GRAIN_ROOT;
}

/**
 * Rewrites a definition's fields into the field tree the contract specifies, so every consumer sees
 * one shape.
 *
 * The drafting agent emits some definitions flat: every column as a single childless node whose name
 * is the whole dotted path (`{name:'projectStatus.project.info'}`) instead of a root node with
 * children. Consumers that walk the tree then see no root and no intermediate nodes, which is not a
 * cosmetic difference - the saved-report eye-options format is rebuilt into a field tree by the
 * graphql server from those node paths, so a flat definition saves a rootless tree and the report
 * re-opens with its columns gone, having rendered correctly in the session that saved it.
 *
 * Splitting each dotted name back into nodes and merging them produces exactly the tree the nested
 * shape would have been, so both save and render identically. A definition already in tree form is
 * returned structurally unchanged. Leaf order is preserved: a node is created where its first column
 * mentions it, which keeps column order stable across a save and re-open.
 */
export function normalizeFieldTree(fields: FieldNode[]): FieldNode[] {
	const roots: FieldNode[] = [];

	const childrenOf = (node: FieldNode): FieldNode[] => {
		if (!node.children) {
			node.children = [];
		}
		return node.children;
	};

	/** The node named `name` among `siblings`, created if absent. Always a NEW object, never one of
	 * the caller's: this is handed `definition.fields` directly, and attaching a child to a node the
	 * definition still holds would corrupt the definition the surface is rendering and saving from. */
	const nodeAt = (siblings: FieldNode[], name: string): FieldNode => {
		const existing = siblings.find(candidate => candidate.name === name);
		if (existing !== undefined) {
			return existing;
		}
		const created: FieldNode = {name};
		siblings.push(created);
		return created;
	};

	/** Fold already-normalised nodes into `siblings`, reusing a node of the same name rather than
	 * appending a second one. */
	const _mergeInto = (siblings: FieldNode[], incoming: FieldNode[]): void => {
		for (const node of incoming) {
			if (typeof node?.name !== 'string') {
				siblings.push(node);
				continue;
			}
			const existing = nodeAt(siblings, node.name);
			if (node.children && node.children.length > 0) {
				_mergeInto(childrenOf(existing), node.children);
			}
			if (existing.children && existing.children.length === 0) {
				delete existing.children;
			}
		}
	};

	for (const field of fields) {
		// A node with no usable name is passed through untouched rather than split. Field names are
		// model-authored and round-trip through storage, and this runs on the SAVE path via
		// `toEyeApplied`: splitting `undefined` throws, which fails the whole save instead of
		// degrading one column. `grainRootOf` above guards the same way.
		if (typeof field?.name !== 'string' || field.name === '') {
			roots.push(field);
			continue;
		}
		let siblings = roots;
		let node: FieldNode | undefined;
		for (const segment of field.name.split('.')) {
			node = nodeAt(siblings, segment);
			siblings = childrenOf(node);
		}
		if (node === undefined) {
			continue;
		}
		if (field.children && field.children.length > 0) {
			// Merged, not pushed: the same parent can be named by both shapes in one definition
			// (`projectStatus.stage` beside `projectStatus` with a `stage` child), and pushing blind
			// gives it two identical children - not the tree the nested shape would have been, which
			// is exactly what this function promises.
			_mergeInto(siblings, normalizeFieldTree(field.children));
		}
		// A leaf must not carry the empty `children` array it picked up on the way down - that would
		// read as a parent with no columns.
		if (node.children && node.children.length === 0) {
			delete node.children;
		}
	}

	return roots;
}
